import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import {
  controlHeightClass,
  errorClass,
  fieldBaseClass,
  fieldBorderClass,
  hintClass,
  labelClass,
} from '@/shared/ui/core/styles';
import type { ControlSize, FieldTone } from '@/shared/ui/core/types';
import { NumberInput, type NumberInputProps } from '@/shared/ui/core/NumberInput';
import { useFormFieldContext } from '@/shared/ui/core/formFieldContext';

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  label?: string;
  hint?: string;
  error?: string;
  tone?: FieldTone;
  size?: ControlSize;
  mono?: boolean;
  required?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  suffix?: ReactNode;
  /**
   * Tự động bôi đen toàn bộ dữ liệu khi click vào input có sẵn data.
   * Mặc định: true.
   */
  autoSelect?: boolean;
  /**
   * Tự động định dạng số theo chuẩn Việt Nam khi người dùng gõ.
   * Ví dụ: 1250000 -> "1.250.000".
   */
  formatNumber?: boolean | Partial<Pick<NumberInputProps, 'decimals' | 'allowNegative' | 'min' | 'max' | 'step' | 'onValueChange' | 'align'>>;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    className,
    label,
    hint,
    error,
    tone,
    size = 'md',
    mono = false,
    startAdornment,
    endAdornment,
    suffix,
    id,
    disabled,
    readOnly,
    autoSelect = true,
    formatNumber,
    ...props
  },
  ref,
) {
  const effectiveEnd = suffix ?? endAdornment;
  if (formatNumber) {
    const formatOpts = typeof formatNumber === 'object' ? formatNumber : {};
    return (
      <NumberInput
        ref={ref}
        className={className}
        label={label}
        hint={hint}
        error={error}
        tone={tone}
        size={size}
        mono={mono}
        required={props.required}
        startAdornment={startAdornment}
        endAdornment={effectiveEnd}
        suffix={suffix}
        id={id}
        disabled={disabled}
        readOnly={readOnly}
        autoSelect={autoSelect}
        {...formatOpts}
        {...(props as any)}
      />
    );
  }

  const autoId = useId();
  const inputId = id ?? autoId;
  const formField = useFormFieldContext();
  const effectiveError = error || (formField.inFormField ? formField.error : undefined);
  const describedBy = effectiveError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  const resolvedTone: FieldTone = tone ?? (effectiveError ? 'error' : 'default');
  const shouldRenderError = Boolean(effectiveError && (!formField.inFormField || (!formField.error && Boolean(error))));

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label ? (
        <label htmlFor={inputId} className={cn(labelClass, 'flex items-center gap-1')}>
          <span>{label}</span>
          {props.required ? (
            <span className="text-danger font-bold text-sm leading-none" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <div className="relative flex items-center">
        {startAdornment ? (
          <span className="pointer-events-none absolute left-2.5 text-text-muted flex items-center">{startAdornment}</span>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={resolvedTone === 'error' || undefined}
          aria-describedby={describedBy}
          data-auto-select={autoSelect === false ? 'false' : undefined}
          className={cn(
            fieldBaseClass,
            fieldBorderClass[resolvedTone],
            controlHeightClass[size],
            'px-3',
            startAdornment && (size === 'sm' || size === 'md' ? 'pl-6' : 'pl-7'),
            effectiveEnd && 'pr-9',
            mono && 'font-mono tabular-nums',
            className,
          )}
          {...props}
        />
        {effectiveEnd ? (
          <span className="absolute right-2.5 flex items-center text-text-muted">{effectiveEnd}</span>
        ) : null}
      </div>
      {shouldRenderError ? (
        <p id={`${inputId}-error`} className={errorClass} role="alert">
          {effectiveError}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className={hintClass}>
          {hint}
        </p>
      ) : null}
    </div>
  );
});
